import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, X, Loader2, AlertCircle, Lock, Lightbulb, Check, Copy, Hash, RefreshCcw,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { PLATFORM_META, type Platform } from '../lib/platforms';

export type AiIdeasUsage = {
  used: number;
  limit: number;
  remaining: number;
  resetAt: string;
  hasSubscription: boolean;
  planName: string | null;
};

type SelectedAccount = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
};

type Idea = {
  angle: string;
  hook: string;
  sampleCaption: string;
  hashtags: string[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Accounts the user has currently picked as post targets — we use the FIRST one for platform + brand context. */
  accounts: SelectedAccount[];
  /** Called when the user clicks "Usar esta idea" on a card. Receives caption + hashtags joined for the composer. */
  onUseIdea: (text: string) => void;
};

export function AiIdeasModal({ open, onClose, accounts, onUseIdea }: Props) {
  const [phase, setPhase] = useState<'idle' | 'generating' | 'result' | 'error'>('idle');
  const [usage, setUsage] = useState<AiIdeasUsage | null>(null);
  const [topic, setTopic] = useState('');
  const [result, setResult] = useState<{ ideas: Idea[]; brandName: string | null; hasBrandContext: boolean } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const firstAccount = accounts[0] ?? null;

  useEffect(() => {
    if (!open) return;
    setPhase('idle');
    setResult(null);
    setErrorMsg(null);
    setErrorCode(null);
    setTopic('');
    setCopiedIdx(null);
    api<{ usage: AiIdeasUsage }>('/api/ai/ideas-usage')
      .then((r) => setUsage(r.usage))
      .catch(() => setUsage(null));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const canRun = !!firstAccount;

  async function run() {
    if (!firstAccount) return;
    setErrorMsg(null);
    setErrorCode(null);
    setResult(null);
    try {
      setPhase('generating');
      const res = await api<{
        id: string;
        ideas: Idea[];
        usage: AiIdeasUsage;
        platform: Platform;
        brandName: string | null;
        hasBrandContext: boolean;
      }>('/api/ai/ideas', {
        method: 'POST',
        body: JSON.stringify({ accountId: firstAccount.id, topic: topic.trim() }),
      });
      setResult({
        ideas: res.ideas,
        brandName: res.brandName,
        hasBrandContext: res.hasBrandContext,
      });
      setUsage(res.usage);
      setPhase('result');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorCode(err.code);
        const m = (err as ApiError & { details?: { message?: string } }).details?.message;
        setErrorMsg(m || err.message || 'Algo ha fallado.');
      } else {
        setErrorMsg(err instanceof Error ? err.message : 'Algo ha fallado.');
      }
      setPhase('error');
    }
  }

  if (!open) return null;

  const meta = firstAccount ? PLATFORM_META[firstAccount.platform] : null;
  const Icon = meta?.icon ?? null;

  function ideaText(idea: Idea): string {
    const tags = idea.hashtags.length > 0
      ? '\n\n' + idea.hashtags.map((h) => `#${h}`).join(' ')
      : '';
    return idea.sampleCaption + tags;
  }

  async function handleCopy(idea: Idea, idx: number) {
    try {
      await navigator.clipboard.writeText(ideaText(idea));
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1800);
    } catch {/* clipboard unavailable */}
  }

  function handleUse(idea: Idea) {
    onUseIdea(ideaText(idea));
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 transition"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center text-white shadow-lg shadow-amber-500/20">
              <Lightbulb size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Ideas de contenido con IA</h2>
              <p className="text-xs text-slate-500">Te damos 10 ideas listas para publicar, adaptadas a tu marca y red social.</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Gating */}
          {phase === 'idle' && usage && !usage.hasSubscription && (
            <UpgradeCta title="Necesitas una suscripción activa" subtitle="La generación de ideas con IA está disponible con cualquier plan." onClose={onClose} />
          )}
          {phase === 'idle' && usage && usage.hasSubscription && usage.limit <= 0 && (
            <UpgradeCta title="Tu plan no incluye generación de ideas" subtitle="Mejora tu plan para usar esta función." onClose={onClose} />
          )}
          {phase === 'idle' && usage && usage.hasSubscription && usage.limit > 0 && usage.remaining <= 0 && (
            <UpgradeCta
              title={`Has usado ${usage.used}/${usage.limit} generaciones este mes`}
              subtitle="Mejora tu plan para tener más disponibles."
              onClose={onClose}
            />
          )}

          {/* Pre-run form */}
          {phase === 'idle' && (!usage || (usage.hasSubscription && usage.limit > 0 && usage.remaining > 0)) && (
            <div>
              {/* Target */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 mb-5">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Plataforma destino</div>
                {!firstAccount && (
                  <p className="text-sm text-slate-500">Selecciona al menos una red social en el composer para generar ideas.</p>
                )}
                {firstAccount && Icon && meta && (
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                      <Icon size={18} color={meta.color} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{meta.label}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {firstAccount.username ? `@${firstAccount.username}` : (firstAccount.displayName ?? 'Cuenta sin nombre')}
                      </div>
                    </div>
                  </div>
                )}
                {accounts.length > 1 && (
                  <p className="mt-3 text-[11px] text-slate-500">
                    Has seleccionado {accounts.length} cuentas. Adaptaremos las ideas a la primera ({meta?.label}).
                  </p>
                )}
              </div>

              {/* Topic */}
              <div className="mb-5">
                <label className="text-sm font-semibold text-slate-800 mb-1.5 block">
                  ¿Sobre qué quieres ideas? <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  value={topic}
                  onChange={(e) => setTopic(e.target.value.slice(0, 1000))}
                  rows={3}
                  placeholder="Ej: lanzamiento de la nueva colección, contenido educativo sobre nutrición, ideas para Black Friday…"
                  className="input resize-y"
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-400">
                    Déjalo vacío para ideas genéricas según tu marca.
                  </p>
                  <span className="text-[11px] text-slate-400 tabular-nums">{topic.length}/1000</span>
                </div>
              </div>

              {usage && <UsagePill usage={usage} className="mb-4" />}

              <button
                onClick={run}
                disabled={!canRun}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-br from-amber-500 to-pink-500 text-white font-semibold shadow-lg shadow-amber-500/20 hover:shadow-xl hover:from-amber-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <Sparkles size={16} /> Generar 10 ideas
              </button>
              {!canRun && (
                <p className="mt-2 text-xs text-center text-slate-500">
                  Selecciona al menos una red social en el composer.
                </p>
              )}
            </div>
          )}

          {phase === 'generating' && (
            <div className="py-10 flex flex-col items-center justify-center text-center">
              <div className="relative mb-6">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center text-white shadow-xl shadow-amber-500/30">
                  <Lightbulb size={26} className="animate-pulse" />
                </div>
                <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-amber-400/30 to-pink-500/30 blur-xl animate-pulse" />
              </div>
              <div className="flex items-center gap-2 text-slate-700 font-medium">
                <Loader2 size={16} className="animate-spin" />
                Pensando ideas…
              </div>
              <p className="mt-2 text-xs text-slate-500 max-w-xs">
                Esto puede tardar entre 10 y 20 segundos. No cierres esta ventana.
              </p>
            </div>
          )}

          {phase === 'error' && (
            <>
              {errorCode === 'SUBSCRIPTION_REQUIRED' || errorCode === 'NOT_INCLUDED_IN_PLAN' ? (
                <UpgradeCta title={errorCode === 'SUBSCRIPTION_REQUIRED' ? 'Suscripción requerida' : 'No incluido en tu plan'} subtitle={errorMsg ?? ''} onClose={onClose} />
              ) : errorCode === 'LIMIT_REACHED' ? (
                <UpgradeCta title="Límite mensual alcanzado" subtitle={errorMsg ?? ''} onClose={onClose} />
              ) : (
                <div className="text-center py-6">
                  <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 mb-3">
                    <AlertCircle size={22} />
                  </div>
                  <h3 className="font-semibold text-slate-900">No pudimos generar ideas</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{errorMsg}</p>
                  <div className="mt-5 flex items-center justify-center gap-2">
                    {canRun && (
                      <button onClick={run} className="btn-primary">Volver a intentar</button>
                    )}
                    <button onClick={onClose} className="btn-secondary">Cerrar</button>
                  </div>
                  {usage && <UsagePill usage={usage} className="mt-5" />}
                </div>
              )}
            </>
          )}

          {phase === 'result' && result && (
            <div>
              {/* Header chips */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {meta && Icon && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
                      <Icon size={12} color={meta.color} /> {meta.label}
                    </span>
                  )}
                  {result.hasBrandContext && result.brandName && (
                    <span className="text-xs text-slate-500">· Marca: <span className="font-medium text-slate-700">{result.brandName}</span></span>
                  )}
                </div>
                <button onClick={run} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
                  <RefreshCcw size={13} /> Generar de nuevo
                </button>
              </div>

              {/* Ideas grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                {result.ideas.map((idea, idx) => (
                  <IdeaCard
                    key={idx}
                    idea={idea}
                    onUse={() => handleUse(idea)}
                    onCopy={() => handleCopy(idea, idx)}
                    copied={copiedIdx === idx}
                  />
                ))}
              </div>

              {usage && <UsagePill usage={usage} className="mb-2" />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Cards & Helpers ─────────────────────────── */

function IdeaCard({
  idea, onUse, onCopy, copied,
}: { idea: Idea; onUse: () => void; onCopy: () => void; copied: boolean }) {
  return (
    <div className="group relative rounded-2xl border border-slate-200 bg-gradient-to-br from-amber-50/40 via-white to-pink-50/30 p-4 flex flex-col transition hover:border-amber-300 hover:shadow-md">
      {idea.angle && (
        <span className="inline-flex self-start text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded-full mb-2">
          {idea.angle}
        </span>
      )}
      <h3 className="text-[15px] font-bold text-slate-900 leading-snug mb-2">{idea.hook}</h3>
      <p className="text-[13px] text-slate-600 leading-relaxed whitespace-pre-wrap flex-1">
        {idea.sampleCaption}
      </p>
      {idea.hashtags.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
            <Hash size={9} /> hashtags
          </div>
          <div className="flex flex-wrap gap-1">
            {idea.hashtags.map((h) => (
              <span key={h} className="text-[11px] font-medium text-brand-700 bg-brand-50 border border-brand-100 px-1.5 py-0.5 rounded">
                #{h}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={onUse}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-br from-amber-500 to-pink-500 text-white text-xs font-semibold shadow-sm hover:shadow-md transition"
        >
          <Check size={12} /> Usar esta idea
        </button>
        <button
          onClick={onCopy}
          className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-600 hover:border-slate-300 transition"
          title="Copiar al portapapeles"
        >
          {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
        </button>
      </div>
    </div>
  );
}

function UsagePill({ usage, className = '' }: { usage: AiIdeasUsage; className?: string }) {
  const pct = usage.limit > 0 ? Math.min(100, (usage.used / usage.limit) * 100) : 0;
  return (
    <div className={`rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 ${className}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600">
          {usage.planName ? (
            <span className="font-medium text-slate-700">Plan {usage.planName}</span>
          ) : (
            <span className="text-slate-500">Sin suscripción</span>
          )}
        </span>
        <span className="text-slate-900 font-medium tabular-nums">
          {usage.used} / {usage.limit} este mes
        </span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-500 to-pink-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function UpgradeCta({ title, subtitle, onClose }: { title: string; subtitle: string; onClose: () => void }) {
  return (
    <div className="text-center py-4">
      <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 mb-3">
        <Lock size={20} />
      </div>
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{subtitle}</p>
      <div className="mt-5 flex items-center justify-center gap-2">
        <Link to="/dashboard/billing" className="btn-primary">Ver planes</Link>
        <button onClick={onClose} className="btn-secondary">Cerrar</button>
      </div>
    </div>
  );
}
